import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Shield,
  Building2,
  Users,
  FileText,
  AlertTriangle,
  Activity,
  Loader2,
  Settings,
  Database,
  Server,
  CheckCircle,
  XCircle,
  Globe,
  Cpu,
  HardDrive,
} from 'lucide-react';
import { toast } from 'sonner';

export const SuperAdminConsolePage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      toast.error('Access denied');
      return;
    }
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get('/dashboard/superadmin');
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch superadmin dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="system-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">System Management</h1>
          <p className="text-muted-foreground mt-1">
            Platform monitoring, system health, and global configuration
          </p>
        </div>
        <Badge className="bg-purple-100 text-purple-700 gap-1 self-start">
          <Shield className="w-3 h-3" />
          Super Admin Access
        </Badge>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Organizations</p>
                <p className="text-2xl font-bold">{data?.org_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold">{data?.total_users || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Reports</p>
                <p className="text-2xl font-bold">{data?.total_reports || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Alerts</p>
                <p className="text-2xl font-bold">{data?.total_alerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="health">
        <TabsList>
          <TabsTrigger value="health" data-testid="health-tab">
            <Activity className="w-4 h-4 mr-2" />
            Platform Health
          </TabsTrigger>
          <TabsTrigger value="config" data-testid="config-tab">
            <Settings className="w-4 h-4 mr-2" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="metrics" data-testid="metrics-tab">
            <Cpu className="w-4 h-4 mr-2" />
            System Metrics
          </TabsTrigger>
        </TabsList>

        {/* Platform Health Tab */}
        <TabsContent value="health" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Service Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  Service Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-slate-500" />
                      <span className="font-medium">API Server</span>
                    </div>
                    <Badge className="bg-green-100 text-green-700 gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Healthy
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Database className="w-5 h-5 text-slate-500" />
                      <span className="font-medium">MongoDB</span>
                    </div>
                    <Badge className="bg-green-100 text-green-700 gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Connected
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Cpu className="w-5 h-5 text-slate-500" />
                      <span className="font-medium">AI Service (GPT-5.2)</span>
                    </div>
                    <Badge className="bg-green-100 text-green-700 gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Available
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <HardDrive className="w-5 h-5 text-slate-500" />
                      <span className="font-medium">HealthVault EMR</span>
                    </div>
                    <Badge className="bg-yellow-100 text-yellow-700 gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Mock Mode
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Organization Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Organization Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Organization</TableHead>
                        <TableHead className="text-center">Users</TableHead>
                        <TableHead className="text-center">Reports</TableHead>
                        <TableHead className="text-center">Alerts</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.org_stats?.map((org) => (
                        <TableRow key={org.org_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{org.org_name}</p>
                              <p className="text-xs text-muted-foreground">{org.org_code}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{org.users}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{org.reports}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={org.pending_alerts > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                              {org.pending_alerts}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Global Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">AI Analysis Settings</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Model</span>
                        <span>GPT-5.2</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Auto-Process Reports</span>
                        <Badge className="bg-green-100 text-green-700">Enabled</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Confidence Threshold</span>
                        <span>0.85</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">Alert Configuration</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Critical Alert Escalation</span>
                        <span>15 min</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">High Alert Escalation</span>
                        <span>1 hour</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Email Notifications</span>
                        <Badge className="bg-yellow-100 text-yellow-700">Pending Setup</Badge>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">Integration Status</h4>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">HealthVault EMR</span>
                      <Badge className="bg-yellow-100 text-yellow-700">Mock</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Lab Connector</span>
                      <Badge className="bg-slate-100 text-slate-700">Not Configured</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Radiology PACS</span>
                      <Badge className="bg-slate-100 text-slate-700">Not Configured</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Metrics Tab */}
        <TabsContent value="metrics" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">System Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="p-4 border rounded-lg text-center">
                  <p className="text-3xl font-bold text-blue-600">{data?.total_reports || 0}</p>
                  <p className="text-sm text-muted-foreground mt-1">Reports Processed (All Time)</p>
                </div>
                <div className="p-4 border rounded-lg text-center">
                  <p className="text-3xl font-bold text-green-600">{data?.total_users || 0}</p>
                  <p className="text-sm text-muted-foreground mt-1">Active Users</p>
                </div>
                <div className="p-4 border rounded-lg text-center">
                  <p className="text-3xl font-bold text-purple-600">{data?.org_count || 0}</p>
                  <p className="text-sm text-muted-foreground mt-1">Organizations</p>
                </div>
              </div>
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  Detailed system metrics and performance dashboards coming soon.
                  Real-time monitoring for API latency, AI processing times, and database performance.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SuperAdminConsolePage;
